import { useEffect, useRef, useState } from "react";
import { LuFlame, LuHourglass, LuTurtle, LuZap } from "react-icons/lu";
import { Avatar } from "../components/Avatar";
import { Ring } from "../components/Arena";
import { FeedbackBox, QuestionCard, revealFor, useCountdown, type FB, type QView } from "../components/QuestionCard";
import { banner, pop, useOnChange, useRecent, useTween } from "../components/juice";
import { buzz, play } from "../sound";

import type { GameProps } from "../pages/Match";

function useServerClock(serverNow?: number) {
  const off = useRef(0);
  const [, tick] = useState(0);
  useEffect(() => { if (serverNow) off.current = serverNow - Date.now(); }, [serverNow]);
  useEffect(() => { const id = setInterval(() => tick((n) => n + 1), 250); return () => clearInterval(id); }, []);
  return () => Date.now() + off.current;
}
const nameOf = (v: any, seat: number) => v.players.find((p: any) => p.seat === seat)?.username ?? "?";

/** "Klar… 3 · 2 · 1" – with a beep for every number and a "go" sound + banner when the game starts */
function Countdown({ view }: { view: any }) {
  const st = view.state;
  const left = useCountdown(st.countdownEnd ?? st.phaseEnd, st.now);
  const on = st.phase === "countdown";
  const n = Math.max(1, Math.ceil(left / 1000));
  useOnChange(on ? n : 0, (now: number, before: number) => {
    if (now > 0) play("count");
    else if (before > 0) { play("go"); banner("Af sted!", "gold"); buzz(40); }
  }, view.id);
  if (!on) return null;
  return <div className="notice big-countdown" role="status" aria-live="assertive"><strong style={{ fontSize: "2rem" }}>Klar… <span key={n} className="cdnum">{n}</span></strong></div>;
}

/* ───────────── Quizduel ───────────── */
function ScoreChip({ name, score, me }: { name: string; score: number; me: boolean }) {
  const v = useTween(score);
  return <span className={`pill scorechip ${me ? "me" : ""}`}>{name}: <strong>{v}</strong> point</span>;
}

export function Quizduel(p: GameProps) {
  const st = p.view.state;
  const reveal = st.reveal;
  const nextLeft = useCountdown(st.nextAt ?? undefined, st.now);
  /* remember the question and my pick, so that the answer can be shown on the card itself once the round is over */
  const lastQ = useRef<QView | null>(null);
  const picked = useRef<number | string | null>(null);
  if (st.question && lastQ.current?.pid !== st.question.pid) { lastQ.current = st.question; picked.current = null; }
  const onAnswer = (g: number | string) => { picked.current = g; return p.answer(g); };
  const mineRes = reveal?.results?.find((r: any) => r.seat === st.you);
  const myScore = (st.live.find((l: any) => l.seat === st.you)?.score ?? 0) as number;
  useOnChange(myScore, (n: number, b: number) => { if (n > b) { pop(`+${n - b}`, "gold"); } }, p.view.id);
  useOnChange(st.round.idx as number, (i: number) => { if (i === st.round.total - 1) banner("Sidste runde!", "pink"); }, p.view.id);
  const total = st.round.total as number, idx = Math.min(st.round.idx, total - 1) as number;
  const fb: FB | null = reveal ? { correct: !!mineRes?.correct, timedOut: !!mineRes && !mineRes.answered, correctText: reveal.correctText, explanation: reveal.explanation, counted: true } : null;
  const showCard = !!(reveal && lastQ.current && lastQ.current.text === reveal.text && fb);
  const waitingOn = st.round.answeredCount as number;
  return (
    <div className="stack duel">
      <div className="row between" role="status" aria-live="polite">
        <span className="pill">Runde {idx + 1} af {total}</span>
        <div className="row">
          {st.live.map((l: any) => <ScoreChip key={l.seat} name={nameOf(p.view, l.seat)} score={l.score} me={l.seat === st.you} />)}
        </div>
      </div>
      <div className="pips" aria-hidden="true">{Array.from({ length: total }, (_, i) => <i key={i} className={i < idx || (i === idx && (st.phase === "reveal" || st.phase === "over")) ? "done" : i === idx ? "now" : ""} />)}</div>
      {st.live.length === 2 && (() => { const a = st.live[0].score, c = st.live[1].score, t = Math.max(1, a + c); return <div className="scorebar" aria-hidden="true"><i style={{ width: `${(a / t) * 100}%` }} /><i style={{ width: `${(c / t) * 100}%` }} /></div>; })()}
      <Countdown view={p.view} />
      {st.phase === "question" && st.question && (
        <>
          <QuestionCard q={st.question} serverNow={st.now} onAnswer={onAnswer} busy={p.busy} locked={!!st.question.answered} totalMs={(st.timerSec ?? 15) * 1000} />
          {st.question.answered
            ? <div className="notice thinking" role="status">Dit svar er låst. Venter på modstanderen…</div>
            : waitingOn > 0 && <div className="notice warn" role="status" style={{ textAlign: "center" }}>Modstanderen har allerede svaret – nu er det din tur!</div>}
        </>
      )}
      {reveal && (st.phase === "reveal" || st.phase === "over") && (
        <>
          {showCard
            ? <QuestionCard q={lastQ.current!} reveal={revealFor(lastQ.current!, picked.current, fb!)} onAnswer={() => undefined} />
            : (
              <div className="fbbox feedback good" role="status" aria-live="polite">
                <div className="fbtext"><p className="fbtitle">Rigtigt svar: {reveal.correctText}</p>
                <p>{reveal.text}</p>
                <p className="muted">{reveal.explanation}</p></div>
              </div>
            )}
          <div className="duelres" role="status">
            {reveal.results.map((r: any) => {
              const pl = p.view.players.find((x: any) => x.seat === r.seat);
              return (
                <div key={r.seat} className={`dr ${r.correct ? "ok" : "no"} ${r.seat === st.you ? "me" : ""}`}>
                  <Avatar equipped={pl?.avatar} size={36} label="" />
                  <div><b>{nameOf(p.view, r.seat)}</b><span>{r.correct ? `Rigtigt, +${r.points} point` : r.answered ? "Forkert" : "Intet svar"}</span></div>
                  {r.correct && <i className="drpts">+{r.points}</i>}
                </div>
              );
            })}
          </div>
          {st.phase === "reveal" && <div className="nextbar"><i style={{ transform: `scaleX(${Math.max(0, Math.min(1, nextLeft / 4000))})` }} /><small className="muted">Næste spørgsmål om {Math.ceil(nextLeft / 1000)} s…</small></div>}
        </>
      )}
    </div>
  );
}

/* ───────────── Speedrun ───────────── */
export function Speedrun(p: GameProps) {
  const st = p.view.state;
  const now = useServerClock(st.now);
  const end = st.endAt as number;
  const left = Math.max(0, end - now());
  const total = (st.timerSec ?? 60) * 1000;
  const me = st.live.find((l: any) => l.seat === st.you);
  const lockLeft = st.lockUntil ? Math.max(0, st.lockUntil - now()) : 0;
  const [last, setLast] = useState<any>(null);
  useEffect(() => { if (p.feedback) setLast(p.feedback); }, [p.feedback]);
  const over = st.phase === "over";
  const running = st.phase !== "countdown" && !over;
  const secs = Math.ceil(left / 1000);
  const ranking = [...st.live].sort((a: any, c: any) => c.score - a.score);
  const score = (me?.score ?? 0) as number;
  const shown = useTween(score);
  const hurry = running && secs <= 10;
  useOnChange(running ? secs : -1, (n: number) => { if (n > 0 && n <= 5) play("tickHi"); else if (n > 5 && n <= 10) play("tick"); }, p.view.id);
  useOnChange(score, (n: number, b: number) => { const d = n - b; if (d > 0) pop(`+${d}`, "good"); else if (d < 0) pop(`${d}`, "soft"); }, p.view.id);
  useOnChange(hurry, (h: boolean) => { if (h) banner("Sidste 10 sekunder!", "pink"); }, p.view.id);
  return (
    <div className="arena-grid">
      <div className="sidecol l">
        <section className="card" aria-label="Live placering">
          <div className="panel-title">Live placering</div>
          <ol className="rank-list">
            {ranking.map((l: any, i: number) => <li key={l.seat} className={l.seat === st.you ? "me" : ""}><span className="pos">{i + 1}</span>{nameOf(p.view, l.seat)}<span className="score">{l.score}</span></li>)}
          </ol>
        </section>
      </div>
      <div className="arena-center">
        <Countdown view={p.view} />
        {!over && lockLeft > 0 && <div className="notice warn lockwait" role="status"><LuHourglass size={18} aria-hidden="true" /> Forkert svar – vent {Math.ceil(lockLeft / 1000)} s…</div>}
        {!over && st.question && lockLeft <= 0 && st.phase !== "countdown" && (
          <QuestionCard q={st.question} serverNow={st.now} onAnswer={p.answer} busy={p.busy} showTimer={false} />
        )}
        {!over && (st.feedback ?? last) && <FeedbackBox fb={st.feedback ?? last} />}
        {over && me && <div className="notice good" role="status">Tiden er gået! Du fik {me.score} point og svarede på {me.answered} spørgsmål.</div>}
        <div className="motto">Hurtigt og præcist</div>
      </div>
      <div className="sidecol r">
        <section className={`card center ${hurry ? "hurry" : ""}`} role="status">
          <Ring value={left} max={total} big={secs} label="sek." />
          <div className="bigscore" aria-label={`Din score: ${score} point`}><LuZap size={20} aria-hidden="true" /><b>{shown}</b><small>point</small></div>
          <span className={`combo ${st.streak >= 3 ? "hot" : ""}`} style={{ ["--s" as any]: Math.min(st.streak, 8) }}><LuFlame size={16} aria-hidden="true" /> Stribe <strong>{st.streak}</strong></span>
        </section>
      </div>
    </div>
  );
}

/* ───────────── Vidensræs ───────────── */
function Lane({ l, name, mine, avatar, len, rank }: { l: any; name: string; mine: boolean; avatar: any; len: number; rank: number }) {
  const pct = Math.max(0, Math.min(100, (l.pos / len) * 100));
  const moving = useRecent(Math.floor(l.pos), 1200);
  return (
    <div className={`lane ${mine ? "me" : ""} ${moving ? "moving" : ""} ${l.locked ? "locked" : ""} ${l.finished ? "done" : ""}`}>
      <div className="row between lane-head">
        <strong><span className="rankdot">{rank}</span>{name}{mine ? " (dig)" : ""}</strong>
        <span className="muted">{Math.round(l.pos)} / {len} m{l.finished ? " – i mål" : ""}{l.locked ? " · bremset" : ""}</span>
      </div>
      <div className="race" role="progressbar" aria-valuemin={0} aria-valuemax={len} aria-valuenow={Math.round(l.pos)} aria-label={`${name} er nået ${Math.round(l.pos)} meter`}>
        {[25, 50, 75].map((m) => <span key={m} className="mk" style={{ left: `${m}%` }}><i>{Math.round((len * m) / 100)} m</i></span>)}
        <div className="runner" style={{ left: `${Math.max(4, Math.min(96, pct))}%` }}>
          <span className="wind" aria-hidden="true"><i /><i /><i /></span>
          <Avatar equipped={avatar} size={42} label="" />
          {l.locked && <span className="snail" aria-hidden="true"><LuTurtle size={16} /></span>}
        </div>
        <div className="finish" />
      </div>
    </div>
  );
}

export function Videnraes(p: GameProps) {
  const st = p.view.state;
  const len: number = st.raceLength || 100;
  const lockLeft = useCountdown(st.lockUntil ?? undefined, st.now);
  const over = st.phase === "over";
  const ranking = [...st.live].sort((a: any, c: any) => c.pos - a.pos);
  const myRank = ranking.findIndex((l: any) => l.seat === st.you) + 1;
  const myPos = (st.live.find((l: any) => l.seat === st.you)?.pos ?? 0) as number;
  useOnChange(myRank, (now: number, before: number) => {
    if (!before || ranking.length < 2 || myPos < 1) return;
    if (now < before) {
      play("pass"); buzz(25);
      if (now === 1) banner("Du fører!", "gold"); else pop("Du overhaler!", "good");
    }
  }, p.view.id);
  useOnChange(Math.floor(myPos / 25), (n: number, b: number) => { if (n > b && myPos < len) pop(`${n * 25} m`, "blue"); }, p.view.id);
  return (
    <div className="arena-grid">
      <div className="sidecol l">
        <section className="card" aria-label="Placering">
          <div className="panel-title">Placering</div>
          <ol className="rank-list">
            {ranking.map((l: any, i: number) => <li key={l.seat} className={l.seat === st.you ? "me" : ""}><span className="pos">{i + 1}</span>{nameOf(p.view, l.seat)}<span className="score">{Math.round(l.pos)} m</span></li>)}
          </ol>
        </section>
      </div>
      <div className="arena-center">
        <Countdown view={p.view} />
        <div className="card stack raceboard" aria-label="Løbsbane">
          {st.live.map((l: any) => {
            const pl = p.view.players.find((x: any) => x.seat === l.seat);
            return <Lane key={l.seat} l={l} name={nameOf(p.view, l.seat)} mine={l.seat === st.you} avatar={pl?.avatar} len={len} rank={ranking.findIndex((r: any) => r.seat === l.seat) + 1} />;
          })}
        </div>
        {!over && lockLeft > 0 && <div className="notice warn lockwait" role="status"><LuHourglass size={18} aria-hidden="true" /> Forkert svar – du er bremset i {Math.ceil(lockLeft / 1000)} s…</div>}
        {!over && st.question && lockLeft <= 0 && st.phase !== "countdown" && <QuestionCard q={st.question} serverNow={st.now} onAnswer={p.answer} busy={p.busy} showTimer={false} />}
        {!over && st.feedback && <FeedbackBox fb={st.feedback} />}
        {over && <div className="notice good" role="status">Løbet er slut!</div>}
        <div className="motto">Svar rigtigt – løb hurtigere</div>
      </div>
      <div className="sidecol r" />
    </div>
  );
}
